import { REGISTRATION_TYPE_STEP_INDEX } from '@/constants/constants';
import IsNextYearEarlyRegistrationContext from '@/contexts/IsNextYearEarlyRegistrationContext';
import { StepContext } from '@/contexts/StepContext';
import useSponsors from '@/hooks/useSponsors';
import { Grid, Paper, Typography } from '@mui/material';
import { Checkbox, IFormLookupFilterProps, Select, Text } from '@ngt/forms';
import { Condition, ConditionMode, useScopedField } from '@ngt/forms-core';
import { useContext, useMemo } from 'react';
import * as Dtos from '../../../api/dtos';
import RegistrationField from '../../RegistrationField';
import SponsorLevel from '../SponsorLevel';

const valueSubscription = {
    value: true
};

const studentOrRegistrarOrTrialCoordinatorSelectedCondition = ({value}: any) => value?.student === true || 
    value?.trialCoordinator === true || 
    value?.registrar === true;

const RegistrationStepTwo = () => {
    const filterTypes = useMemo(() => {
        const output: IFormLookupFilterProps<Dtos.Registration> = {
            filterFunction: (formState, _propertyPath, lookup) => {
                if (!formState.value?.staffConfirmed) {
                    return {
                        ...lookup,
                        items: lookup?.items?.filter(x => x.id !== Dtos.RegistrationType.Staff && 
                            x.id !== Dtos.RegistrationType.Speaker && 
                            x.id !== Dtos.RegistrationType.EarlyBird) 
                    }
                }
                
                return {
                    ...lookup,
                    items: lookup?.items?.filter(x => x.id !== Dtos.RegistrationType.EarlyBird) 
                }
            },
            subscription: valueSubscription
        }

        return output;
    }, []);

    const {
        state: {
            value: sponsor
        }} = useScopedField<boolean>('sponsor', valueSubscription);

    const { sponsors } = useSponsors();

    const isNextYearEarlyRegistration = useContext(IsNextYearEarlyRegistrationContext);

    return (
        <StepContext.Provider value={REGISTRATION_TYPE_STEP_INDEX}>
            <Paper
                sx={{ p: 4, pt: 0, mt: { xs: 1, md: 3 } }}
            >
                <Grid 
                    container
                    columnSpacing={4}
                >
                    <Grid
                        item
                        xs={12}
                        md={6}
                        mt={4}
                    >
                        {
                            sponsor?
                                <>
                                    <RegistrationField
                                        label="Sponsor"
                                        propertyPath="sponsorId"
                                        component={Select}
                                        sx={{margin:"normal"}}
                                        nullOption={false}
                                        FormControlProps={{
                                            variant: "standard"
                                        }}
                                        options={sponsors?.map((sponsor) => ({ value: sponsor.sponsorName, id: sponsor.id }))}
                                    />
                                    <SponsorLevel />
                                </>
                                : isNextYearEarlyRegistration ?
                                <>
                                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                        Please only select this if you are registering as a trial coordinator for next year's event.
                                    </Typography>
                                    <RegistrationField
                                        label="I am registering as a trial coordinator"
                                        propertyPath="trialCoordinator"
                                        component={Checkbox}
                                    />
                                </>
                                :
                                <RegistrationField
                                    label="Registration Type"
                                    propertyPath="registrationType"
                                    component={Select}
                                    nullOption={false}
                                    FormControlProps={{
                                        variant: "standard"
                                    }}
                                    LookupFilterProps={filterTypes}
                                /> 
                        }
                    </Grid>
                </Grid>
                <Condition
                    condition={studentOrRegistrarOrTrialCoordinatorSelectedCondition}
                    subscription={valueSubscription}
                    mode={ConditionMode.Show}
                >
                    <Grid 
                        container 
                        columnSpacing={4}
                    >
                        <Grid 
                            item
                            xs={12}
                            md={12}
                            mt={4}
                        >
                            <RegistrationField
                                label="Please enter the name of your supervisor"
                                propertyPath="supervisorName"
                                component={Text}
                                variant="standard"
                            />
                        </Grid>
                    </Grid> 
                </Condition>
            </Paper>
        </StepContext.Provider>
    );
}

export default RegistrationStepTwo;